'use client'

import { useCallback, useEffect, useId, useRef, useState } from 'react'
import { extractYoutubeVideoId, extractTweetStatusId } from '@/lib/comment-embed-pipeline'
import { loadPlyrFromCdn, PLYR_CDN_ASSETS } from '@/lib/plyr-cdn'
import { PLYR_I18N } from '@/lib/plyr-options'
import {
  extractDailymotionVideoId,
  extractFacebookVideoHref,
  extractVimeoVideoId,
  resolveVideoPlayerType,
  VIDEO_PLAYER_TYPE,
} from '@/lib/video-utils'
import { getMasterVideoPath, snapToQualityLadder } from '@/lib/video-qualities'
import { getImageUrl, getMediaFileUrl } from '@/lib/utils'
import { IMAGE_PRESETS } from '@/lib/image-presets'
import { API_URL } from '@/lib/config'
import { loadYoutubeIframeApi, type YTPlayer } from '@/lib/load-youtube-iframe-api'
import styles from './VideoPlayer.module.css'

interface VideoPlayerProps {
  video?: string | null
  youtube?: string | null
  dailymotion?: string | null
  vimeo?: string | null
  x?: string | null
  facebook?: string | null
  playerType?: number | null
  image?: string | null
  title?: string
  autoPlay?: boolean
  initialVolume?: number
  aspectRatio?: '16:9' | '9:16'
  className?: string
  resolveQualities?: boolean
}

type PlyrInstance = {
  destroy: () => void
  source: unknown
  on: (event: string, cb: (...args: unknown[]) => void) => void
  media?: HTMLVideoElement
  duration?: number
  quality?: number
  playing?: boolean
  muted?: boolean
  volume?: number
  play?: () => Promise<void> | void
  pause?: () => void
}

type QualitySource = { size: number; src: string; type: string }
type NativeEmbedProvider = 'youtube' | 'dailymotion' | 'vimeo' | 'x' | 'facebook'
type NativeEmbedHandle = {
  provider: NativeEmbedProvider
  iframe: HTMLIFrameElement | null
  youtubePlayer?: YTPlayer
  playing: boolean
  mutedAutoplayFallbackAttempted?: boolean
}

function clampInitialVolume(value: number): number {
  return Math.min(100, Math.max(0, Number.isFinite(value) ? Math.round(value) : 50))
}

function pauseNativeEmbed(embed: NativeEmbedHandle) {
  if (typeof embed.youtubePlayer?.pauseVideo === 'function') {
    embed.youtubePlayer.pauseVideo()
    embed.playing = false
    return
  }

  const target = embed.iframe?.contentWindow
  if (!target) return

  try {
    if (embed.provider === 'youtube') {
      target.postMessage(
        JSON.stringify({ event: 'command', func: 'pauseVideo', args: [] }),
        'https://www.youtube.com',
      )
    } else if (embed.provider === 'vimeo') {
      target.postMessage(JSON.stringify({ method: 'pause' }), 'https://player.vimeo.com')
    } else if (embed.provider === 'dailymotion') {
      target.postMessage('pause', '*')
    }
    embed.playing = false
  } catch {
    /* Provider may not expose a postMessage playback API. */
  }
}

function playNativeEmbed(embed: NativeEmbedHandle) {
  if (typeof embed.youtubePlayer?.playVideo === 'function') {
    embed.youtubePlayer.playVideo()
    return
  }

  const target = embed.iframe?.contentWindow
  if (!target) return

  try {
    if (embed.provider === 'youtube') {
      target.postMessage(
        JSON.stringify({ event: 'command', func: 'playVideo', args: [] }),
        'https://www.youtube.com',
      )
    } else if (embed.provider === 'vimeo') {
      target.postMessage(JSON.stringify({ method: 'play' }), 'https://player.vimeo.com')
    } else if (embed.provider === 'dailymotion') {
      target.postMessage('play', '*')
    }
  } catch {
    /* Provider may not expose a postMessage playback API. */
  }
}

function setNativeEmbedVolume(
  embed: NativeEmbedHandle,
  volume: number,
) {
  if (typeof embed.youtubePlayer?.setVolume === 'function') {
    embed.youtubePlayer.setVolume(clampInitialVolume(volume))
    return
  }

  const target = embed.iframe?.contentWindow
  if (!target) return

  try {
    if (embed.provider === 'youtube') {
      target.postMessage(
        JSON.stringify({
          event: 'command',
          func: 'setVolume',
          args: [clampInitialVolume(volume)],
        }),
        'https://www.youtube.com',
      )
    } else if (embed.provider === 'vimeo') {
      target.postMessage(
        JSON.stringify({
          method: 'setVolume',
          value: clampInitialVolume(volume) / 100,
        }),
        'https://player.vimeo.com',
      )
    }
  } catch {
    /* Provider may not expose a postMessage volume API. */
  }
}

function mimeForUrl(url: string): string {
  const lower = url.toLowerCase()
  if (lower.includes('.webm')) return 'video/webm'
  if (lower.includes('.mov')) return 'video/quicktime'
  return 'video/mp4'
}

function formatPlayerTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds <= 0) return '00:00'
  const total = Math.floor(seconds)
  const hours = Math.floor(total / 3600)
  const minutes = Math.floor((total % 3600) / 60)
  const remaining = total % 60
  if (hours > 0) {
    return `${hours.toString().padStart(2, '0')}:${minutes
      .toString()
      .padStart(2, '0')}:${remaining.toString().padStart(2, '0')}`
  }
  return `${minutes.toString().padStart(2, '0')}:${remaining.toString().padStart(2, '0')}`
}

function probeVideoHeight(src: string): Promise<number> {
  return new Promise((resolve) => {
    const v = document.createElement('video')
    v.preload = 'metadata'
    v.muted = true
    v.playsInline = true
    const done = (h: number) => {
      v.removeAttribute('src')
      v.load()
      resolve(h)
    }
    v.onloadedmetadata = () => done(v.videoHeight || 0)
    v.onerror = () => done(0)
    window.setTimeout(() => done(v.videoHeight || 0), 8000)
    v.src = src
  })
}

async function fetchQualitySources(masterPath: string): Promise<QualitySource[]> {
  const master = getMasterVideoPath(masterPath)
  const fallbackSrc = masterPath.startsWith('http') ? masterPath : getMediaFileUrl(master)

  try {
    const apiBase = String(API_URL || '').replace(/\/$/, '')
    const res = await fetch(`${apiBase}/media/video-sources?path=${encodeURIComponent(master)}`, {
      credentials: 'omit',
    })
    if (res.ok) {
      const data = (await res.json()) as {
        sources?: { size: number; path: string; url: string }[]
      }
      if (data.sources?.length) {
        const mapped = data.sources
          .map((s) => ({
            size: Number(s.size) || 0,
            src: s.url || getMediaFileUrl(s.path),
            path: s.path || '',
            type: mimeForUrl(s.url || s.path),
          }))
          .filter((s) => !!s.src)

        // Always measure master (no _q) — never trust invented ladder labels
        let masterSize = 0
        for (const s of mapped) {
          const isMaster = !/_q\d+\./i.test(s.path) && !/_q\d+\./i.test(s.src)
          if (isMaster || s.size <= 0) {
            const h = await probeVideoHeight(s.src)
            if (h > 0) {
              s.size = snapToQualityLadder(h)
              if (isMaster) masterSize = s.size
            } else if (s.size <= 0) {
              s.size = 720
              if (isMaster) masterSize = 720
            }
          }
        }
        if (!masterSize) {
          const m = mapped.find((s) => !/_q\d+\./i.test(s.path) && !/_q\d+\./i.test(s.src))
          masterSize = m?.size || 0
        }

        // Drop upscaled / fake ladder files taller than (or equal to) master
        const usable = mapped.filter((s) => {
          const isMaster = !/_q\d+\./i.test(s.path) && !/_q\d+\./i.test(s.src)
          if (isMaster) return s.size > 0
          if (!masterSize) return s.size > 0
          return s.size < masterSize - 32
        })

        // One entry per resolution (prefer explicit _q file over master duplicate)
        const bySize = new Map<number, (typeof usable)[number]>()
        for (const s of usable) {
          if (s.size <= 0) continue
          const prev = bySize.get(s.size)
          const sIsVariant = /_q\d+\./i.test(s.path) || /_q\d+\./i.test(s.src)
          const prevIsVariant = prev
            ? /_q\d+\./i.test(prev.path) || /_q\d+\./i.test(prev.src)
            : false
          if (!prev || (sIsVariant && !prevIsVariant)) {
            bySize.set(s.size, s)
          }
        }

        const unique = [...bySize.values()]
          .map(({ size, src, type }) => ({ size, src, type }))
          .sort((a, b) => b.size - a.size)
        if (unique.length) return unique
      }
    }
  } catch {
    /* fall through */
  }

  const h = await probeVideoHeight(fallbackSrc)
  return [
    {
      size: h > 0 ? snapToQualityLadder(h) : 1080,
      src: fallbackSrc,
      type: mimeForUrl(fallbackSrc),
    },
  ]
}

/** Pick best available quality for viewport + network (never invent missing sizes). */
function pickDefaultQuality(sizes: number[]): number {
  if (!sizes.length) return 720
  const sorted = [...sizes].sort((a, b) => b - a)

  const dpr = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1
  const cssW = typeof window !== 'undefined' ? window.innerWidth : 1280
  const displayPx = cssW * dpr
  const displayCap =
    displayPx <= 640
      ? 480
      : displayPx <= 1100
        ? 720
        : displayPx <= 1600
          ? 1080
          : displayPx <= 2560
            ? 1440
            : 2160

  let netCap = 1080
  try {
    const nav = navigator as Navigator & {
      connection?: {
        effectiveType?: string
        downlink?: number
        saveData?: boolean
      }
      mozConnection?: {
        effectiveType?: string
        downlink?: number
        saveData?: boolean
      }
      webkitConnection?: {
        effectiveType?: string
        downlink?: number
        saveData?: boolean
      }
    }
    const conn = nav.connection || nav.mozConnection || nav.webkitConnection
    if (conn) {
      const et = String(conn.effectiveType || '')
      const downlink = Number(conn.downlink) || 0
      if (conn.saveData) netCap = 360
      else if (et === 'slow-2g' || et === '2g') netCap = 360
      else if (et === '3g') netCap = 480
      else if (downlink > 0 && downlink < 1.5) netCap = 480
      else if (downlink > 0 && downlink < 5) netCap = 720
      else if (downlink >= 15) netCap = 2160
      else netCap = 1080
    }
  } catch {
    /* ignore */
  }

  const target = Math.min(displayCap, netCap)
  const atOrBelow = sorted.filter((q) => q <= target)
  if (atOrBelow.length) return atOrBelow[0]
  return sorted[sorted.length - 1]
}

function buildPlyrOptions(sources: QualitySource[]) {
  const sizes = [...new Set(sources.map((s) => s.size))].sort((a, b) => b - a)
  const multi = sizes.length > 1
  const defaultQuality = pickDefaultQuality(sizes)

  return {
    // İlerleme ve toplam süre her boyutta görünür kalır.
    controls: [
      'play-large',
      'play',
      'progress',
      'current-time',
      'duration',
      'mute',
      'volume',
      'captions',
      'settings',
      'pip',
      'airplay',
      'fullscreen',
    ],
    settings: multi ? ['captions', 'quality', 'speed'] : ['captions', 'speed'],
    speed: { selected: 1, options: [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2] },
    quality: {
      default: defaultQuality,
      options: sizes.length ? sizes : [defaultQuality],
      forced: false,
    },
    ratio: '16:9',
    seekTime: 10,
    volume: 1,
    muted: false,
    clickToPlay: true,
    hideControls: true,
    resetOnEnd: false,
    keyboard: { focused: true, global: false },
    tooltips: { controls: true, seek: true },
    displayDuration: true,
    invertTime: false,
    toggleInvert: false,
    disableContextMenu: true,
    loadSprite: true,
    iconUrl: PLYR_CDN_ASSETS.iconUrl,
    blankVideo: PLYR_CDN_ASSETS.blankVideo,
    i18n: PLYR_I18N,
  }
}

export default function VideoPlayer({
  video,
  youtube,
  dailymotion,
  vimeo,
  x,
  facebook,
  playerType,
  image,
  title,
  autoPlay = false,
  initialVolume = 50,
  aspectRatio = '16:9',
  className = '',
  resolveQualities = true,
}: VideoPlayerProps) {
  const reactId = useId().replace(/:/g, '')
  const mountRef = useRef<HTMLDivElement>(null)
  const shellRef = useRef<HTMLDivElement>(null)
  const playerRef = useRef<PlyrInstance | null>(null)
  const nativeEmbedRef = useRef<NativeEmbedHandle | null>(null)
  const resumeAfterHiddenRef = useRef(false)
  const isVisibleRef = useRef(true)
  const readyRef = useRef(false)
  const autoPlayRef = useRef(autoPlay)
  const initialVolumeRef = useRef(clampInitialVolume(initialVolume))
  const autoplayAppliedRef = useRef(false)
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading')

  const markReady = () => {
    readyRef.current = true
    setStatus('ready')
  }

  const type = resolveVideoPlayerType(playerType, youtube, dailymotion, video, vimeo, x, facebook)
  const poster = image ? getImageUrl(image, IMAGE_PRESETS.videoPoster) : undefined
  const youtubeId =
    type === VIDEO_PLAYER_TYPE.YOUTUBE && youtube
      ? extractYoutubeVideoId(youtube) || youtube.trim()
      : null
  const dailymotionId =
    type === VIDEO_PLAYER_TYPE.DAILYMOTION && dailymotion
      ? extractDailymotionVideoId(dailymotion) || dailymotion.trim()
      : null
  const vimeoId =
    type === VIDEO_PLAYER_TYPE.VIMEO && vimeo ? extractVimeoVideoId(vimeo) || vimeo.trim() : null
  const xId = type === VIDEO_PLAYER_TYPE.X && x ? extractTweetStatusId(x) || x.trim() : null
  const facebookHref =
    type === VIDEO_PLAYER_TYPE.FACEBOOK && facebook
      ? extractFacebookVideoHref(facebook) || facebook.trim()
      : null
  const mp4Path = type === VIDEO_PLAYER_TYPE.MP4 && video ? video.trim() : null

  useEffect(() => {
    autoPlayRef.current = autoPlay
  }, [autoPlay])

  useEffect(() => {
    const nextVolume = clampInitialVolume(initialVolume)
    initialVolumeRef.current = nextVolume
    const player = playerRef.current
    if (player) {
      player.volume = nextVolume / 100
    } else {
      const nativeMedia = mountRef.current?.querySelector('video')
      if (nativeMedia) nativeMedia.volume = nextVolume / 100
    }

    const nativeEmbed = nativeEmbedRef.current
    if (nativeEmbed) {
      setNativeEmbedVolume(nativeEmbed, nextVolume)
    }
  }, [initialVolume])

  const requestConfiguredAutoplay = useCallback(() => {
    if (!autoPlayRef.current || autoplayAppliedRef.current) return

    const player = playerRef.current
    const media = player ? null : mountRef.current?.querySelector('video')
    const play = player?.play ? () => player.play?.() : media ? () => media.play() : null
    if (!play) return

    const volume = initialVolumeRef.current / 100
    if (player) {
      player.volume = volume
      player.muted = false
    }
    if (media) {
      media.volume = volume
      media.muted = false
    }

    autoplayAppliedRef.current = true
    const attemptPlay = () => Promise.resolve().then(() => play())
    void attemptPlay().catch(() => {
      // Browsers may reject autoplay with sound. Preserve the configured
      // volume, fall back to muted playback, and let the native/Plyr control
      // restore that same level on the first user interaction.
      if (player) player.muted = true
      if (media) media.muted = true
      void attemptPlay().catch(() => {
        autoplayAppliedRef.current = false
      })
    })
  }, [])

  useEffect(() => {
    const root = mountRef.current
    if (!root) return

    let cancelled = false
    let cleanupSeekPreview: (() => void) | null = null
    let cleanupCompactVolume: (() => void) | null = null
    let cleanupSurfaceToggle: (() => void) | null = null
    let pendingYoutubePlayer: YTPlayer | null = null
    readyRef.current = false
    resumeAfterHiddenRef.current = false
    autoplayAppliedRef.current = false
    queueMicrotask(() => {
      if (!cancelled && !readyRef.current) setStatus('loading')
    })

    const destroy = () => {
      cleanupSeekPreview?.()
      cleanupSeekPreview = null
      cleanupCompactVolume?.()
      cleanupCompactVolume = null
      cleanupSurfaceToggle?.()
      cleanupSurfaceToggle = null
      const nativeEmbed = nativeEmbedRef.current
      if (nativeEmbed) {
        pauseNativeEmbed(nativeEmbed)
        const youtubePlayer = nativeEmbed.youtubePlayer ?? pendingYoutubePlayer
        try {
          if (typeof youtubePlayer?.destroy === 'function') {
            youtubePlayer.destroy()
          }
        } catch {
          /* The YouTube player may already have removed its iframe. */
        }
        pendingYoutubePlayer = null
        nativeEmbedRef.current = null
      }
      if (playerRef.current) {
        try {
          playerRef.current.destroy()
        } catch {
          /* ignore */
        }
        playerRef.current = null
      }
      root.innerHTML = ''
    }

    destroy()

    const bindSeekPreview = (player: PlyrInstance) => {
      cleanupSeekPreview?.()
      cleanupSeekPreview = null
      const seekInput = root.querySelector<HTMLInputElement>("input[data-plyr='seek']")
      const seekTooltip =
        seekInput?.closest('.plyr__progress')?.querySelector<HTMLElement>('.plyr__tooltip') ?? null
      if (!seekInput || !seekTooltip) return

      const updateSeekPreview = () => {
        const value = Number(seekInput.value)
        const max = Number(seekInput.max) || 100
        const mediaDuration = player.duration || player.media?.duration || 0
        const selectedTime =
          max > 0 && mediaDuration > 0
            ? (Math.min(max, Math.max(0, value)) / max) * mediaDuration
            : 0
        seekTooltip.textContent = formatPlayerTime(selectedTime)
      }
      seekInput.addEventListener('input', updateSeekPreview)
      seekInput.addEventListener('change', updateSeekPreview)
      seekInput.addEventListener('pointerdown', updateSeekPreview)
      cleanupSeekPreview = () => {
        seekInput.removeEventListener('input', updateSeekPreview)
        seekInput.removeEventListener('change', updateSeekPreview)
        seekInput.removeEventListener('pointerdown', updateSeekPreview)
      }
    }

    const bindCompactVolume = () => {
      cleanupCompactVolume?.()
      cleanupCompactVolume = null

      let closeTimer: number | undefined
      const reveal = () => {
        const playerRoot = root.querySelector<HTMLElement>('.plyr')
        if (!playerRoot) return
        playerRoot.classList.add('volume-open')
        if (closeTimer) window.clearTimeout(closeTimer)
        closeTimer = window.setTimeout(() => {
          playerRoot.classList.remove('volume-open')
          closeTimer = undefined
        }, 3200)
      }
      const handlePointer = (event: Event) => {
        const target = event.target
        if (!(target instanceof Element)) return
        if (target.closest("button[data-plyr='mute'], .plyr__volume input[type='range']")) {
          reveal()
        }
      }

      root.addEventListener('click', handlePointer, true)
      root.addEventListener('pointerdown', handlePointer, true)
      cleanupCompactVolume = () => {
        if (closeTimer) window.clearTimeout(closeTimer)
        root.querySelector<HTMLElement>('.plyr')?.classList.remove('volume-open')
        root.removeEventListener('click', handlePointer, true)
        root.removeEventListener('pointerdown', handlePointer, true)
      }
    }

    const bindPlayerSurfaceToggle = (player: PlyrInstance) => {
      cleanupSurfaceToggle?.()
      cleanupSurfaceToggle = null

      const handleSurfaceClick = (event: MouseEvent) => {
        const target = event.target
        if (!(target instanceof Element) || target.closest('.plyr__controls')) return
        if (!target.closest('.plyr')) return

        event.preventDefault()
        event.stopPropagation()
        event.stopImmediatePropagation()
        if (player.playing) {
          player.pause?.()
        } else if (player.play) {
          void Promise.resolve(player.play()).catch(() => {})
        }
      }

      root.addEventListener('click', handleSurfaceClick, true)
      cleanupSurfaceToggle = () => {
        root.removeEventListener('click', handleSurfaceClick, true)
      }
    }

    const mountIframe = (
      src: string,
      label: string,
      allow: string,
      provider: NativeEmbedProvider,
      loading: 'eager' | 'lazy' = 'lazy',
    ) => {
      const wrap = document.createElement('div')
      wrap.className = styles.embedFrame
      const iframe = document.createElement('iframe')
      iframe.src = src
      iframe.allow = allow
      iframe.allowFullscreen = true
      iframe.referrerPolicy = 'strict-origin-when-cross-origin'
      iframe.title = title || label
      iframe.setAttribute('loading', loading)
      iframe.addEventListener(
        'load',
        () => {
          const embed = nativeEmbedRef.current
          if (!embed || embed.iframe !== iframe) return
          setNativeEmbedVolume(embed, initialVolumeRef.current)
          if (!isVisibleRef.current) {
            resumeAfterHiddenRef.current = autoPlayRef.current
            pauseNativeEmbed(embed)
          }
        },
        { once: true },
      )
      wrap.appendChild(iframe)
      root.appendChild(wrap)
      nativeEmbedRef.current = {
        iframe,
        provider,
        playing: autoPlayRef.current && isVisibleRef.current,
      }
      markReady()
      return destroy
    }

    if (youtubeId) {
      const wrap = document.createElement('div')
      wrap.className = styles.embedFrame
      const host = document.createElement('div')
      host.id = `youtube-player-${reactId}`
      wrap.appendChild(host)
      root.appendChild(wrap)

      const handle: NativeEmbedHandle = {
        provider: 'youtube',
        iframe: null,
        playing: false,
      }
      nativeEmbedRef.current = handle

      void loadYoutubeIframeApi()
        .then((YT) => {
          if (cancelled || !mountRef.current) return
          pendingYoutubePlayer = new YT.Player(host, {
            videoId: youtubeId,
            playerVars: {
              autoplay: 0,
              controls: 1,
              playsinline: 1,
              mute: 0,
              rel: 0,
              origin: window.location.origin,
            },
            events: {
              onReady: (event) => {
                if (cancelled) return
                handle.youtubePlayer = event.target
                pendingYoutubePlayer = null
                handle.iframe = event.target.getIframe()
                setNativeEmbedVolume(handle, initialVolumeRef.current)
                markReady()
                if (autoPlayRef.current && isVisibleRef.current) {
                  event.target.unMute()
                  playNativeEmbed(handle)
                } else if (autoPlayRef.current) {
                  resumeAfterHiddenRef.current = true
                }
              },
              onStateChange: (event) => {
                if (cancelled) return
                handle.playing = event.data === YT.PlayerState.PLAYING
              },
              onAutoplayBlocked: () => {
                if (cancelled) return
                handle.playing = false
                if (
                  autoPlayRef.current &&
                  isVisibleRef.current &&
                  !handle.mutedAutoplayFallbackAttempted
                ) {
                  handle.mutedAutoplayFallbackAttempted = true
                  handle.youtubePlayer?.mute()
                  playNativeEmbed(handle)
                  return
                }
                markReady()
              },
              onError: () => {
                if (!cancelled) setStatus('error')
              },
            },
          })
        })
        .catch(() => {
          if (cancelled) return
          root.innerHTML = ''
          const origin = encodeURIComponent(window.location.origin)
          mountIframe(
            `https://www.youtube.com/embed/${encodeURIComponent(youtubeId)}?playsinline=1&rel=0&controls=1&enablejsapi=1&origin=${origin}&autoplay=${autoPlayRef.current ? '1' : '0'}&mute=${autoPlayRef.current ? '1' : '0'}`,
            'YouTube video',
            'autoplay; encrypted-media; picture-in-picture; fullscreen; web-share',
            'youtube',
            'eager',
          )
        })

      return () => {
        cancelled = true
        destroy()
      }
    }

    if (dailymotionId) {
      return mountIframe(
        `https://www.dailymotion.com/embed/video/${encodeURIComponent(dailymotionId)}?controls=1&api=postMessage&autoplay=${autoPlay ? '1' : '0'}${autoPlay ? '&mute=1' : ''}`,
        'Dailymotion video',
        'autoplay; fullscreen; picture-in-picture; encrypted-media; web-share',
        'dailymotion',
        'eager',
      )
    }

    if (vimeoId) {
      return mountIframe(
        `https://player.vimeo.com/video/${encodeURIComponent(vimeoId)}?controls=1&playsinline=1&autoplay=${autoPlay ? '1' : '0'}${autoPlay ? '&muted=1' : ''}`,
        'Vimeo video',
        'autoplay; fullscreen; picture-in-picture; encrypted-media',
        'vimeo',
        'eager',
      )
    }

    if (xId) {
      return mountIframe(
        `https://platform.twitter.com/embed/Tweet.html?id=${encodeURIComponent(xId)}`,
        'X gönderisi',
        'autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share',
        'x',
      )
    }

    if (facebookHref) {
      return mountIframe(
        `https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(facebookHref)}&show_text=false&width=560`,
        'Facebook videosu',
        'autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share',
        'facebook',
      )
    }

    if (!mp4Path) {
      queueMicrotask(() => {
        if (!cancelled) setStatus('error')
      })
      return destroy
    }

    void (async () => {
      try {
        const Plyr = await loadPlyrFromCdn()
        if (cancelled || !mountRef.current) return
        root.innerHTML = ''

        const directSrc = /^(?:https?:|blob:|data:)/i.test(mp4Path)
          ? mp4Path
          : getMediaFileUrl(mp4Path)
        const sources = resolveQualities
          ? await fetchQualitySources(mp4Path)
          : [{ size: 0, src: directSrc, type: mimeForUrl(directSrc) }]
        if (cancelled || !mountRef.current) return

        const ordered = [...sources].sort((a, b) => b.size - a.size)
        const primary = ordered[0]
        if (!primary?.src) {
          setStatus('error')
          return
        }

        const videoEl = document.createElement('video')
        videoEl.id = `plyr-${reactId}`
        videoEl.playsInline = true
        videoEl.setAttribute('playsinline', '')
        videoEl.setAttribute('webkit-playsinline', '')
        videoEl.preload = 'metadata'
        if (poster) videoEl.setAttribute('poster', poster)
        if (title) videoEl.setAttribute('aria-label', title)

        root.appendChild(videoEl)

        const player = new Plyr(videoEl, buildPlyrOptions(ordered)) as PlyrInstance
        playerRef.current = player

        player.source = {
          type: 'video',
          title: title || 'Video',
          poster: poster || undefined,
          sources: ordered.map((s) => ({
            src: s.src,
            type: s.type,
            size: s.size,
          })),
        }
        player.volume = initialVolumeRef.current / 100

        bindSeekPreview(player)
        bindCompactVolume()
        bindPlayerSurfaceToggle(player)

        const fallbackNative = () => {
          if (cancelled) return
          cleanupSeekPreview?.()
          cleanupSeekPreview = null
          cleanupCompactVolume?.()
          cleanupCompactVolume = null
          cleanupSurfaceToggle?.()
          cleanupSurfaceToggle = null
          try {
            player.destroy()
          } catch {
            /* ignore */
          }
          playerRef.current = null
          root.innerHTML = ''
          const native = document.createElement('video')
          native.className = styles.nativeVideo
          native.controls = true
          native.playsInline = true
          native.preload = 'metadata'
          native.volume = initialVolumeRef.current / 100
          native.src = primary.src
          if (poster) native.poster = poster
          root.appendChild(native)
          markReady()
          requestConfiguredAutoplay()
        }

        player.on('ready', () => {
          if (!cancelled) {
            bindSeekPreview(player)
            bindCompactVolume()
            bindPlayerSurfaceToggle(player)
            markReady()
            requestConfiguredAutoplay()
          }
        })
        player.on('error', fallbackNative)

        window.setTimeout(() => {
          if (cancelled || readyRef.current) return
          const media = player.media
          if (media && media.readyState === 0 && media.networkState === 3) {
            fallbackNative()
          } else {
            markReady()
          }
        }, 6000)
      } catch (err) {
        console.error('[VideoPlayer] Plyr init failed', err)
        if (cancelled || !mountRef.current) return
        const src = /^(?:https?:|blob:|data:)/i.test(mp4Path)
          ? mp4Path
          : getMediaFileUrl(getMasterVideoPath(mp4Path))
        root.innerHTML = ''
        const native = document.createElement('video')
        native.className = styles.nativeVideo
        native.controls = true
        native.playsInline = true
        native.volume = initialVolumeRef.current / 100
        native.src = src
        if (poster) native.poster = poster
        root.appendChild(native)
        markReady()
        requestConfiguredAutoplay()
      }
    })()

    return () => {
      cancelled = true
      destroy()
    }
  }, [
    aspectRatio,
    mp4Path,
    youtubeId,
    dailymotionId,
    vimeoId,
    xId,
    facebookHref,
    poster,
    title,
    reactId,
    requestConfiguredAutoplay,
    resolveQualities,
    autoPlay,
  ])

  useEffect(() => {
    if (autoPlay) requestConfiguredAutoplay()
  }, [autoPlay, requestConfiguredAutoplay])

  useEffect(() => {
    const shell = shellRef.current
    if (!shell || typeof IntersectionObserver === 'undefined') return

    const observer = new IntersectionObserver(
      ([entry]) => {
        isVisibleRef.current = entry.isIntersecting && entry.intersectionRatio >= 0.35
        const player = playerRef.current
        const native = mountRef.current?.querySelector('video')
        const isPlaying = player?.playing ?? (native ? !native.paused : false)

        if (!isVisibleRef.current) {
          const nativeEmbed = nativeEmbedRef.current
          if (nativeEmbed) {
            if (nativeEmbed.playing || autoPlayRef.current) {
              resumeAfterHiddenRef.current = true
            }
            pauseNativeEmbed(nativeEmbed)
            return
          }
          if (isPlaying) resumeAfterHiddenRef.current = true
          if (!isPlaying) return
          if (player?.pause) player.pause()
          else if (native) native.pause()
          return
        }

        if (!resumeAfterHiddenRef.current) return
        resumeAfterHiddenRef.current = false
        const nativeEmbed = nativeEmbedRef.current
        if (nativeEmbed) {
          playNativeEmbed(nativeEmbed)
        } else if (player?.play) {
          void Promise.resolve(player.play()).catch(() => {})
        } else if (native) {
          void native.play().catch(() => {})
        }
      },
      { threshold: [0, 0.35, 0.6] },
    )

    observer.observe(shell)
    return () => observer.disconnect()
  }, [])

  if (!mp4Path && !youtubeId && !dailymotionId && !vimeoId && !xId && !facebookHref) return null

  return (
    <div
      ref={shellRef}
      className={`${styles.shell} ${xId || facebookHref ? styles.shellTall : ''} haber-plyr mb-2 sm:mb-2.5 ${className}`}
      style={xId || facebookHref ? undefined : { aspectRatio: aspectRatio.replace(':', ' / ') }}
    >
      {status === 'loading' && (
        <div className={styles.loading} aria-hidden>
          <span className={styles.loadingSpinner} />
        </div>
      )}
      {status === 'error' && <div className={styles.errorBox}>Video yüklenemedi</div>}
      <div ref={mountRef} className={styles.mount} />
    </div>
  )
}
